/**
 * InvestigationApprovalsPage (Phase-2, Module 6 — admin/coordinator side)
 *
 * Approve requested investigations, assign a lab, mark in-progress, and reject.
 * Report upload + completion happen on the lab side (LabInvestigationsPage).
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, IconButton, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Biotech as InvIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, ConfirmDialog } from '../../components/common';
import { InvestigationApi, LaboratoryApi } from '../../services/phase2Api';
import { TEST_TYPES, labelFor } from '../../utils/phase2Constants';
import type { InvestigationRequest, Laboratory } from '../../types/phase2';

const InvestigationApprovalsPage: React.FC = () => {
  const [rows, setRows] = useState<InvestigationRequest[]>([]);
  const [labs, setLabs] = useState<Laboratory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [assignId, setAssignId] = useState<number | null>(null);
  const [labChoice, setLabChoice] = useState<number | ''>('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [page, l] = await Promise.all([InvestigationApi.listAll({ page: 0, size: 100 }), LaboratoryApi.getActive()]);
      setRows(page?.content || []); setLabs(l || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load investigations'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<InvestigationRequest>, label: string) => {
    try { await fn(); load(); } catch (e: any) { setError(e?.response?.data?.message || `${label} failed`); }
  };

  const handleReject = async (reason?: string) => {
    if (rejectId == null || !reason) return;
    try { await InvestigationApi.reject(rejectId, reason); setRejectId(null); load(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Reject failed'); }
  };

  const handleAssign = async () => {
    if (assignId == null || !labChoice) return;
    try { await InvestigationApi.assignLab(assignId, Number(labChoice)); setAssignId(null); setLabChoice(''); load(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Assign failed'); }
  };

  const columns: GridColDef[] = [
    { field: 'investigationNumber', headerName: 'Inv. No.', width: 140 },
    { field: 'officerName', headerName: 'Officer', width: 150, renderCell: (p) => p.value || '—' },
    { field: 'testType', headerName: 'Test Type', width: 140, renderCell: (p) => <Chip size="small" label={labelFor(TEST_TYPES, p.value)} variant="outlined" /> },
    { field: 'testName', headerName: 'Test', flex: 1.1, minWidth: 140, renderCell: (p) => p.value || p.row.customTestName || '—' },
    { field: 'priority', headerName: 'Priority', width: 100 },
    { field: 'labName', headerName: 'Lab', width: 140, renderCell: (p) => p.value || '—' },
    { field: 'status', headerName: 'Status', width: 140, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Actions', width: 300, sortable: false,
      renderCell: (p) => {
        const s = p.row.status as string;
        const id = p.row.id as number;
        const btn = (label: string, fn: () => Promise<InvestigationRequest>, color?: 'primary' | 'success' | 'warning') => (
          <Button size="small" variant="contained" color={color || 'primary'} onClick={() => act(fn, label)} sx={{ textTransform: 'none', mr: 0.5 }}>{label}</Button>
        );
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            {s === 'REQUESTED' && btn('Approve', () => InvestigationApi.approve(id), 'success')}
            {s === 'APPROVED' && <Button size="small" variant="contained" color="warning" onClick={() => setAssignId(id)} sx={{ textTransform: 'none', mr: 0.5 }}>Assign Lab</Button>}
            {(s === 'ASSIGNED' || s === 'SAMPLE_COLLECTED') && btn('In Progress', () => InvestigationApi.markInProgress(id), 'primary')}
            {(s === 'REQUESTED' || s === 'APPROVED') && <Button size="small" variant="outlined" color="error" onClick={() => setRejectId(id)} sx={{ textTransform: 'none' }}>Reject</Button>}
          </Box>
        );
      },
    },
  ];

  return (
    <PageScaffold title="Investigation Approvals" subtitle="Approve, assign lab & track investigations" icon={<InvIcon />}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      <Box sx={{ height: 560, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>

      <ConfirmDialog open={rejectId != null} title="Reject Investigation" message="Provide a reason for rejecting this investigation."
        confirmLabel="Reject" requireReason reasonLabel="Rejection reason" onConfirm={handleReject} onClose={() => setRejectId(null)} />

      <Dialog open={assignId != null} onClose={() => setAssignId(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Assign Laboratory</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Laboratory</InputLabel>
            <Select label="Laboratory" value={labChoice} onChange={(e) => setLabChoice(Number(e.target.value))}>
              {labs.map((l) => <MenuItem key={l.id} value={l.id}>{l.labName}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignId(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={!labChoice} sx={{ textTransform: 'none', fontWeight: 600 }}>Assign</Button>
        </DialogActions>
      </Dialog>
    </PageScaffold>
  );
};

export default InvestigationApprovalsPage;